// ============================================================================
// Clinical text snippets — curated shortcuts for routine clinical
// documentation. Surfaced via the SnippetPicker component (the 💡 button
// next to any long-form field in the encounter editor).
//
// Categories drive the picker's grouping. Each snippet has:
//   - trigger     short canonical name shown in the picker
//   - aliases     extra search terms (so "triple antibiotics" and
//                 "triple therapy" both find the same regimen)
//   - category    grouping label
//   - body        the text that gets inserted into the field
//
// PNG-aware defaults — doses + drugs reflect what's commonly available
// in PNG hospitals + what the National Treatment Protocols recommend.
// Edit / extend freely; this is a flat array so new entries land
// without any schema work.
// ============================================================================

export const CLINICAL_SNIPPETS = [
  // ---- Antibiotic / antimicrobial regimens ------------------------------
  {
    trigger: 'Triple antibiotics (IV)',
    aliases: ['triple therapy', 'sepsis cover', 'broad spectrum iv'],
    category: 'Antibiotics',
    body:
      'Ceftriaxone 1 g IV stat., ' +
      'Metronidazole (Flagyl) 500 mg IV stat., ' +
      'Gentamicin 160 mg IV stat.',
  },
  {
    trigger: 'Sepsis (severe) — empirical',
    aliases: ['septic shock', 'severe sepsis'],
    category: 'Antibiotics',
    body:
      'Ceftriaxone 2 g IV stat., then 1 g IV BD. ' +
      'Metronidazole 500 mg IV TDS. ' +
      'Add Gentamicin 5 mg/kg IV daily if shock or source-unknown. ' +
      'Source control + IV fluids + lactate monitoring.',
  },
  {
    trigger: 'Pneumonia — community acquired',
    aliases: ['cap', 'pneumonia oral'],
    category: 'Antibiotics',
    body:
      'Amoxicillin 1 g PO TDS for 5 days. ' +
      'If severe / hospitalised: Ceftriaxone 1 g IV daily + Azithromycin 500 mg PO daily. ' +
      'Oxygen to maintain SpO₂ ≥ 92%. ' +
      'Review at 48 hours.',
  },
  {
    trigger: 'UTI — uncomplicated',
    aliases: ['cystitis'],
    category: 'Antibiotics',
    body:
      'Trimethoprim 300 mg PO daily for 3 days, OR ' +
      'Nitrofurantoin 100 mg PO QID for 5 days. ' +
      'Fluids + symptom relief. Return if fever / flank pain.',
  },
  {
    trigger: 'Pyelonephritis',
    aliases: ['kidney infection', 'upper uti'],
    category: 'Antibiotics',
    body:
      'Ceftriaxone 1 g IV daily for 7–10 days, step down to oral on clinical improvement. ' +
      'IV fluids, paracetamol PRN. ' +
      'Urine MC&S sent. Review at 48 hours.',
  },

  // ---- TB / malaria / tropical -----------------------------------------
  {
    trigger: 'TB — quadruple therapy (intensive phase)',
    aliases: ['rhze', 'anti tb', 'tb treatment'],
    category: 'TB & Tropical',
    body:
      'RHZE (rifampicin / isoniazid / pyrazinamide / ethambutol) — fixed-dose combination, ' +
      'weight-based daily dosing for 2 months intensive phase. ' +
      'Then RH (rifampicin / isoniazid) for 4 months continuation. ' +
      'DOTS supervision. Liver function + visual acuity baseline.',
  },
  {
    trigger: 'Severe malaria — adult',
    aliases: ['severe malaria', 'cerebral malaria'],
    category: 'TB & Tropical',
    body:
      'Artesunate 2.4 mg/kg IV at 0, 12 and 24 hours, then daily until oral therapy tolerated. ' +
      'Step down to full course of artemether-lumefantrine (Coartem) for 3 days. ' +
      'Glucose monitoring + treat hypoglycaemia. ' +
      'IV fluids cautious — pulmonary oedema risk. ' +
      'Treat any co-existing bacterial sepsis.',
  },
  {
    trigger: 'Uncomplicated malaria',
    aliases: ['simple malaria', 'coartem'],
    category: 'TB & Tropical',
    body:
      'Artemether-lumefantrine (Coartem) — weight-based dosing for 3 days. ' +
      'Take with fatty meal. ' +
      'Paracetamol for fever. ' +
      'Return immediately if vomiting / drowsiness / unable to drink.',
  },
  {
    trigger: 'Dengue — supportive',
    aliases: ['dengue fever'],
    category: 'TB & Tropical',
    body:
      'Bed rest. Oral fluids — ORS preferred. Paracetamol for fever. ' +
      'AVOID NSAIDs and aspirin. ' +
      'Daily FBC (platelet + haematocrit). ' +
      'Warning signs: persistent vomiting, mucosal bleeding, lethargy, abdominal pain — ' +
      'admit for IV fluids if any present.',
  },

  // ---- Common chronic-disease starts ----------------------------------
  {
    trigger: 'Hypertension — newly diagnosed',
    aliases: ['htn start', 'new hypertension'],
    category: 'Chronic disease',
    body:
      'Lifestyle counselling: salt restriction, weight loss, exercise 30 min ≥ 5 days/week, ' +
      'limit alcohol, smoking cessation. ' +
      'Start Amlodipine 5 mg PO daily; titrate to 10 mg if BP not at target in 4 weeks. ' +
      'Add HCTZ 12.5 mg daily if dual therapy needed. ' +
      'Baseline ECG + UEC + lipids + urine ACR. ' +
      'Review BP in 2 weeks.',
  },
  {
    trigger: 'Type 2 diabetes — newly diagnosed',
    aliases: ['t2dm start', 'new diabetes'],
    category: 'Chronic disease',
    body:
      'Lifestyle: low-GI diet, weight loss target 5–10%, exercise 30 min ≥ 5 days/week. ' +
      'Start Metformin 500 mg PO BD with meals; titrate to 1 g BD over 2–4 weeks. ' +
      'Baseline HbA1c + UEC + lipids + urine ACR + retinal screen + foot check. ' +
      'BP target < 130/80. Statin if eligible. ' +
      'Diabetes nurse referral. Review HbA1c in 3 months.',
  },
  {
    trigger: 'Asthma — exacerbation (moderate)',
    aliases: ['asthma flare', 'asthma attack'],
    category: 'Chronic disease',
    body:
      'Salbutamol 5 mg neb (or 6–8 puffs spacer) every 20 min for 1 hour, then PRN. ' +
      'Ipratropium 500 mcg neb if severe. ' +
      'Prednisolone 40–50 mg PO stat, then daily for 5 days. ' +
      'Oxygen to maintain SpO₂ ≥ 94%. ' +
      'Re-check PEF + auscultation post-bronchodilator. ' +
      'Asthma action plan reviewed.',
  },

  // ---- Fluids / supportive --------------------------------------------
  {
    trigger: 'IV fluid — adult resuscitation',
    aliases: ['fluid bolus', 'resus fluids'],
    category: 'Fluids & supportive',
    body:
      'Hartmann\'s (Ringer\'s lactate) 500 mL IV over 15 min. ' +
      'Reassess HR, BP, capillary refill, urine output. ' +
      'Repeat once if hypotensive; total ≤ 30 mL/kg before reassessing for cardiac / pulmonary status.',
  },
  {
    trigger: 'IV fluid — adult maintenance',
    aliases: ['maintenance fluids'],
    category: 'Fluids & supportive',
    body:
      '0.9% Normal Saline OR Hartmann\'s 1 L IV over 8 hours. ' +
      'Add KCl 20 mmol/L if eating poorly and K⁺ < 4. ' +
      'Daily UEC if continued > 24 hours.',
  },
  {
    trigger: 'ORS protocol — adult',
    aliases: ['oral rehydration', 'gastro fluids'],
    category: 'Fluids & supportive',
    body:
      'Oral Rehydration Solution (ORS): 200 mL after each loose stool, ' +
      'plus continued normal fluid intake. ' +
      'Zinc 20 mg PO daily for 10 days. ' +
      'Paracetamol PRN. Avoid antimotility agents. ' +
      'Return if signs of dehydration, fever > 39°C, or blood in stool.',
  },

  // ---- Examination / history templates -------------------------------
  {
    trigger: 'Normal general examination',
    aliases: ['normal exam', 'unremarkable exam'],
    category: 'Examination',
    body:
      'Alert and oriented to time, place, person. No respiratory distress. Well-perfused, no pallor / jaundice / cyanosis / oedema. ' +
      'Cardiovascular: HS I + II, no murmurs. ' +
      'Respiratory: equal air entry bilaterally, vesicular breath sounds, no added sounds. ' +
      'Abdomen: soft, non-tender, no organomegaly, bowel sounds present. ' +
      'Neurological: GCS 15, CN II–XII intact, normal tone / power / reflexes / sensation.',
  },
  {
    trigger: 'URTI — typical history',
    aliases: ['cold history', 'cough cold'],
    category: 'History templates',
    body:
      '3-day history of sore throat, blocked nose, dry cough, ' +
      'low-grade fever (~37.8 °C), mild generalised body aches. ' +
      'No dyspnoea, no chest pain, no haemoptysis. ' +
      'No sick contacts. Eating + drinking normally. Activity reduced.',
  },
  {
    trigger: 'Gastroenteritis — typical history',
    aliases: ['gastro history', 'diarrhoea history'],
    category: 'History templates',
    body:
      '24-hour history of profuse watery diarrhoea (5–6 episodes), ' +
      'associated nausea + non-bilious vomiting. ' +
      'Mild crampy abdominal pain, no blood / mucus in stool. ' +
      'No fever recorded. Reduced oral intake. ' +
      'No recent antibiotic use. No sick contacts.',
  },

  // ---- Pregnancy / women's health ------------------------------------
  {
    trigger: 'ANC routine review',
    aliases: ['antenatal review', 'anc visit'],
    category: 'Women\'s health',
    body:
      'Antenatal review at __ weeks. ' +
      'No vaginal bleeding / abdominal pain / reduced fetal movements / fluid loss. ' +
      'BP, urine dipstick, fundal height, fetal heart rate documented above. ' +
      'Iron + folate continued. Tetanus / malaria prophylaxis up to date per schedule. ' +
      'Counselled on birth-preparedness + danger signs. Next ANC in __ weeks.',
  },
  {
    trigger: 'Postnatal review (6-week)',
    aliases: ['postpartum review', 'pnc'],
    category: 'Women\'s health',
    body:
      '6-week postnatal review. Mother + baby well. Lochia settled, no fever / breast issues. ' +
      'Mood + sleep adequate. ' +
      'Contraception discussed and chosen. ' +
      'Baby weight + immunisations up to date.',
  },

  // ---- Plans / dispositions ------------------------------------------
  {
    trigger: 'Discharge home — stable',
    aliases: ['discharge plan', 'sent home'],
    category: 'Plan & disposition',
    body:
      'Patient clinically stable for discharge. Medications dispensed and explained. ' +
      'Return immediately if: fever > 39 °C, worsening pain, vomiting unable to keep fluids down, ' +
      'shortness of breath, reduced consciousness. ' +
      'Follow-up review in __ days.',
  },
  {
    trigger: 'Refer to specialist',
    aliases: ['referral plan'],
    category: 'Plan & disposition',
    body:
      'Clinical findings discussed with patient. ' +
      'Referring to __ specialty for further assessment / management. ' +
      'Awaiting appointment. Continue current symptomatic therapy. ' +
      'Patient understands. Re-attend earlier if symptoms worsen.',
  },
  {
    trigger: 'Admit for observation',
    aliases: ['admit plan'],
    category: 'Plan & disposition',
    body:
      'Admit to general ward for observation + supportive care. ' +
      'Vital signs 4-hourly. IV access × 1. ' +
      'Bloods: FBC, UEC, LFTs, blood culture if febrile. ' +
      'Daily review by ward team. ' +
      'Discharge criteria documented.',
  },
]

// Group by category for the picker UI.
export function groupSnippetsByCategory(snippets = CLINICAL_SNIPPETS) {
  const buckets = new Map()
  for (const s of snippets) {
    if (!buckets.has(s.category)) buckets.set(s.category, [])
    buckets.get(s.category).push(s)
  }
  return Array.from(buckets.entries()).map(([category, items]) => ({ category, items }))
}

// Filter by query against trigger + aliases + body keywords.
export function searchSnippets(query, snippets = CLINICAL_SNIPPETS) {
  const q = (query || '').trim().toLowerCase()
  if (!q) return snippets
  return snippets.filter((s) => {
    const haystack = [
      s.trigger,
      ...(s.aliases || []),
      s.category,
    ].join(' ').toLowerCase()
    return haystack.includes(q)
  })
}
